import { useState } from 'react';
import { Package, Truck, CheckCircle, ExternalLink, AlertTriangle } from 'lucide-react';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';

const STATUS_LABELS = {
  not_shipped: 'Nicht versendet',
  preparing: 'Vorbereitung',
  shipped: 'Versendet',
  delivered: 'Zugestellt',
  returned: 'Zurück',
  overdue: 'Überfällig'
};

const STATUS_VARIANTS = {
  not_shipped: 'warning',
  preparing: 'info',
  shipped: 'success',
  delivered: 'success',
  returned: 'secondary',
  overdue: 'error'
};

export function ShippingActions({ booking, onUpdate }) {
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [isMarkingShipped, setIsMarkingShipped] = useState(false);

  // Helper function to download and optionally print PDF
  const downloadAndPrintLabel = (labelUrl, bookingId) => {
    // Normalize URL - remove leading /uploads/ if present
    const normalizedUrl = labelUrl.startsWith('uploads/')
      ? `/${labelUrl}`
      : labelUrl.startsWith('/uploads/')
        ? labelUrl
        : `/uploads/${labelUrl}`;

    // Download PDF
    const link = document.createElement('a');
    link.href = normalizedUrl;
    link.download = `DHL-Label-${bookingId}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Open in new tab for printing
    const printWindow = window.open(normalizedUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        // Auto-print after 1 second
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      });
    }
  };

  const handleGenerateLabel = async () => {
    setIsGeneratingLabel(true);
    const loadingToast = toast.loading('Erstelle Versandlabel...');

    try {
      const response = await api.createShippingLabel(booking.bookingId);

      if (response.success) {
        // Auto-download and print label
        downloadAndPrintLabel(response.labelUrl, booking.bookingId);
        toast.success('Versandlabel erstellt!', { id: loadingToast });

        // Trigger refresh
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.error || 'Fehler beim Erstellen des Labels', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error creating shipping label:', error);
      toast.error(error.message || 'Fehler beim Erstellen des Labels', { id: loadingToast });
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const handleMarkAsShipped = async () => {
    if (!booking.trackingNumber) {
      toast.error('Bitte erst Versandlabel erstellen!');
      return;
    }

    const confirmed = window.confirm(
      `Bestellung als versendet markieren?\n\nDer Kunde erhält eine Email mit der Tracking-Information.`
    );

    if (!confirmed) return;

    setIsMarkingShipped(true);
    const loadingToast = toast.loading('Markiere als versendet...');

    try {
      const response = await api.markAsShipped(booking.bookingId);

      if (response.success) {
        toast.success('Bestellung als versendet markiert!', { id: loadingToast });

        // Trigger refresh
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.error || 'Fehler beim Aktualisieren', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error marking as shipped:', error);
      toast.error(error.message || 'Fehler beim Aktualisieren', { id: loadingToast });
    } finally {
      setIsMarkingShipped(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const shippingStatus = booking.shippingStatus || 'not_shipped';

  return (
    <div className="space-y-4">
      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Versandstatus:</span>
        <Badge variant={STATUS_VARIANTS[shippingStatus]}>
          {STATUS_LABELS[shippingStatus]}
        </Badge>
      </div>

      {/* Not Shipped */}
      {shippingStatus === 'not_shipped' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Noch kein Versandlabel erstellt. Erstelle ein DHL-Label, um die Bestellung zu versenden.
          </p>
          <Button
            onClick={handleGenerateLabel}
            disabled={isGeneratingLabel}
            variant="primary"
            size="md"
            className="w-full sm:w-auto"
          >
            <Package className="w-4 h-4 mr-2" />
            {isGeneratingLabel ? 'Erstelle Label...' : 'Versandlabel drucken'}
          </Button>
        </div>
      )}

      {/* Preparing (Label created, not yet shipped) */}
      {shippingStatus === 'preparing' && booking.trackingNumber && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">Sendungsnummer</p>
                <p className="text-lg font-mono text-blue-700">{booking.trackingNumber}</p>
              </div>
              <a
                href={`https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${booking.trackingNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                Tracking öffnen
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {booking.labelUrl && (
            <div className="flex gap-2">
              <Button
                onClick={() => downloadAndPrintLabel(booking.labelUrl, booking.bookingId)}
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Package className="w-4 h-4 mr-2" />
                Erneut drucken
              </Button>
              <Button
                onClick={() => {
                  const labelUrl = booking.labelUrl;
                  const normalizedUrl = labelUrl.startsWith('uploads/')
                    ? `/${labelUrl}`
                    : labelUrl.startsWith('/uploads/')
                      ? labelUrl
                      : `/uploads/${labelUrl}`;
                  window.open(normalizedUrl, '_blank');
                }}
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Anzeigen
              </Button>
            </div>
          )}

          <Button
            onClick={handleMarkAsShipped}
            disabled={isMarkingShipped}
            variant="success"
            size="md"
            className="w-full sm:w-auto"
          >
            <Truck className="w-4 h-4 mr-2" />
            {isMarkingShipped ? 'Markiere...' : 'Als versendet markieren'}
          </Button>
        </div>
      )}

      {/* Shipped */}
      {shippingStatus === 'shipped' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900 mb-1">Versendet</p>
              <p className="text-sm text-green-700">
                {booking.shippedAt && `am ${formatDate(booking.shippedAt)}`}
              </p>
              {booking.trackingNumber && (
                <div className="mt-2">
                  <p className="text-xs text-green-600 mb-1">Sendungsnummer:</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm text-green-800">{booking.trackingNumber}</p>
                    <a
                      href={`https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${booking.trackingNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"
                    >
                      Tracking
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delivered */}
      {shippingStatus === 'delivered' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900 mb-1">Zugestellt</p>
              <p className="text-sm text-green-700">
                {booking.delivered_at && `am ${formatDate(booking.delivered_at)}`}
              </p>
              {booking.trackingNumber && (
                <p className="text-xs text-green-600 mt-2 font-mono">{booking.trackingNumber}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Returned */}
      {shippingStatus === 'returned' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-gray-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 mb-1">Zurückgegeben</p>
              <p className="text-sm text-gray-700">
                {booking.returned_at && `am ${formatDate(booking.returned_at)}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overdue */}
      {shippingStatus === 'overdue' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900 mb-1">Überfällig</p>
              <p className="text-sm text-red-700">
                Die Fotobox wurde noch nicht zurückgegeben.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingActions;
